import Dexie, { Table } from 'dexie';
import type { LegacyCommentRow, LegacyHazardRow, LegacyQueueRow } from './legacyMigration';
import type {
  FunctionAppVersion,
  HazardLexicon,
  Reaction,
  ReactionType,
  SyncInfo,
  TenantInfo,
} from './types';

/** Installed name of the pre-rebuild Dexie database. */
export const LEGACY_DB_NAME = 'HazardDatabase';

/**
 * Read/write handle for an existing HazardDatabase. Versions 21 and 22 match
 * the schema the app last shipped, so opening does not rewrite indexes.
 * This module is the only runtime code that knows that database name.
 */
export class LegacyHazardDatabase extends Dexie {
  syncInfo!: Table<SyncInfo, string>;
  communityHazards!: Table<LegacyHazardRow, string>;
  comments!: Table<LegacyCommentRow, string>;
  reactions!: Table<Reaction, string>;
  reactionTypes!: Table<ReactionType, number>;
  syncQueue!: Table<LegacyQueueRow, number>;
  tenantInfo!: Table<TenantInfo, string>;
  functionAppVersion!: Table<FunctionAppVersion, string>;
  hazardLexicon!: Table<HazardLexicon, number>;

  constructor() {
    super(LEGACY_DB_NAME);
    this.version(22).stores({
      communityHazards: 'id, description, utc_timestamp, latitude, longitude, username, last_synced, user_id, deletedNum, *tags, [deletedNum+utc_timestamp]',
    });
    this.version(21).stores({
      syncInfo: '&key',
      communityHazards: 'id, description, utc_timestamp, latitude, longitude, username, last_synced, user_id, deleted, *tags, [deleted+utc_timestamp]',
      comments: 'id, incident_id, user_id, created_at, last_synced, deleted',
      reactions: 'id, user_id, reaction_type_name, content_type, content_id, created_at, last_synced, deleted, [user_id+reaction_type_name+content_type+content_id]',
      reactionTypes: 'id, reaction_type, content',
      syncQueue: '++id, reaction_id, operation, content_type, content_id, created_at, attempts',
      tenantInfo: 'id, name, subdomain, description, last_updated',
      functionAppVersion: '&key, lastModified, fileSize, timestamp, lastChecked',
      hazardLexicon: 'id, term, category, Cause_Grouping, OSHA_OIICS_Event_or_Exposure, regex_pattern',
    });
  }
}

/** Serializes open/close/delete so two HazardDatabase connections cannot overlap. */
let legacyAccess: Promise<void> = Promise.resolve();
/** Resolves the gate held by the connection returned from openLegacyHazardDatabase. */
let releaseLegacyAccess: (() => void) | null = null;

function enqueueLegacy(task: () => Promise<void>): Promise<void> {
  const run = legacyAccess.then(task, task);
  legacyAccess = run.then(
    () => undefined,
    () => undefined
  );
  return run;
}

/**
 * Open the single shared HazardDatabase connection. The gate stays held until
 * closeLegacyHazardDatabase runs, so Dexie.delete cannot abort another opener.
 */
export function openLegacyHazardDatabase(): Promise<LegacyHazardDatabase | null> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const resolveOnce = (db: LegacyHazardDatabase | null) => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(db);
    };
    const rejectOnce = (error: unknown) => {
      if (settled) {
        return;
      }
      settled = true;
      reject(error);
    };

    enqueueLegacy(async () => {
      let db: LegacyHazardDatabase | null = null;
      try {
        const exists = await Dexie.exists(LEGACY_DB_NAME);
        if (!exists) {
          resolveOnce(null);
          return;
        }
        db = new LegacyHazardDatabase();
        await db.open();
        await new Promise<void>((release) => {
          releaseLegacyAccess = release;
          resolveOnce(db);
        });
      } catch (error) {
        if (db?.isOpen()) {
          db.close();
        }
        releaseLegacyAccess = null;
        rejectOnce(error);
      }
    }).catch((error: unknown) => {
      rejectOnce(error);
    });
  });
}

/**
 * Close the shared connection and, when requested, delete HazardDatabase
 * before any other opener is allowed to run.
 */
export async function closeLegacyHazardDatabase(
  db: LegacyHazardDatabase,
  options?: { deleteDatabase?: boolean }
): Promise<void> {
  const release = releaseLegacyAccess;
  releaseLegacyAccess = null;
  try {
    if (db.isOpen()) {
      db.close();
    }
    if (options?.deleteDatabase) {
      await Dexie.delete(LEGACY_DB_NAME);
    }
  } finally {
    release?.();
  }
}
